import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button, Form, FormGroup, Progress } from "reactstrap";
import api from "../../api/api";
import { useParams } from "react-router-dom";
import ModalMessage from "../ModalMessage/ModalMessage";
import { useDropzone } from "react-dropzone";
import { WarningModal } from "../WarningModal/WarningModal";
import { MdOutlineCloudUpload } from "react-icons/md";

const ModalUploadImageNewMission = forwardRef(({ updateMissions }, ref) => {
  const [loading, setLoading] = useState(false);
  const [folderUploads, setFolderUploads] = useState([]);
  const [currentUploadItem, setCurrentUploadItem] = useState("");
  const [statusEnviandoFilaImagens, setStatusEnviandoFilaImagens] =
    useState(false);
  const [messageModal, setMessageModal] = useState(null);
  const [message, setMessage] = useState();
  const [currentUploadFolder, setCurrentUploadFolder] = useState("");
  const [modal, setModal] = useState();

  const toggleMessageModal = () => setMessageModal(!messageModal);

  const globalAbortController = useRef(null);
  const fileInputRef = useRef(null);

  let { id: projectId } = useParams();

  useImperativeHandle(ref, () => ({
    cancelUploads: () => {
      cancelUpload();
    },
  }));

  const onDrop = useCallback((acceptedFiles) => {
    const allowedTypes = ["image/png", "image/tiff", "image/jpeg", "image/jpg"];

    const newFolders = new Map();

    acceptedFiles.forEach((file) => {
      if (!allowedTypes.includes(file.type.toLowerCase())) return;

      let relativePath = file.webkitRelativePath || file.path || file.name;
      if (relativePath.startsWith("/")) {
        relativePath = relativePath.substring(1);
      }

      const folderName = relativePath.split("/")[0];
      const baseFileName = relativePath.split("/").pop();

      if (!newFolders.has(folderName)) {
        newFolders.set(folderName, {
          folderName,
          files: [],
          missionId: null,
          status: "pending",
        });
      }

      newFolders
        .get(folderName)
        .files.push([file, "Aguardando", "Não Enviado", baseFileName]);
    });

    setFolderUploads((prevFolders) => {
      const prevFolderMap = new Map(prevFolders.map((f) => [f.folderName, f]));

      newFolders.forEach((folderData, folderName) => {
        prevFolderMap.set(folderName, folderData);
      });

      return Array.from(prevFolderMap.values());
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: false,
    webkitdirectory: true,
  });

  const removeFolder = (folderName) => {
    setFolderUploads((prev) =>
      prev.filter((folder) => folder.folderName !== folderName)
    );
  };

  async function createMission(folderName) {
    try {
      setLoading(true);

      const newMissionName = folderName;

      const { data } = await api.post("/missions", {
        name: newMissionName,
        project_id: projectId,
      });

      console.log(data);

      return data.id;
    } catch (e) {
      throw e;
    } finally {
      setLoading(false);
    }
  }

  async function createMissionWithRetry(folder, folderIndex) {
    const initialName = folder.folderName;
    let missionName = initialName || "";
    let success = false;
    let missionId = null;
    let canceled = false;

    while (!success) {
      try {
        const result = await createMission(missionName);
        missionId = result;
        success = true;
      } catch (e) {
        if (e.response?.data === "MISSION_NAME_ALREADY_EXISTS") {
          // eslint-disable-next-line no-loop-func
          const userResponse = await new Promise((resolve) => {
            setModal({
              show: true,
              title: "Missão já existente!",
              message: `Já existe uma missão com o nome "${missionName}". Por favor, insira um novo nome para a missão!`,
              buttonText: "Enviar",
              input: true,
              onConfirm: (newName) => {
                resolve({ action: "retry", newName });
                setModal({ show: false });
              },
              onCancel: () => {
                resolve({ action: "cancel" });
                setModal({ show: false });
              },
            });
          });

          if (userResponse.action === "cancel") {
            removeFolder(initialName);
            canceled = true;
            break;
          }

          missionName = userResponse.newName;
          // eslint-disable-next-line no-loop-func
          setFolderUploads((prev) =>
            prev.map((folder) =>
              folder.folderName === initialName ||
              folder.folderName === missionName
                ? {
                    ...folder,
                    previousName: folder.folderName,
                    folderName: missionName,
                  }
                : folder
            )
          );
        } else {
          setMessage("Erro ao criar missão.");
          toggleMessageModal();
          break;
        }
      }
    }

    return { missionId, canceled };
  }

  const sendImages = useCallback(
    async (
      folderName,
      batch,
      startIndex,
      auxList,
      abortController,
      missionId
    ) => {
      const uploadData = new FormData();

      batch.forEach(([image, , , cleanFileName], index) => {
        auxList[startIndex + index][1] = "Enviando";
        uploadData.append("files", image, cleanFileName);
      });

      setFolderUploads((prev) => {
        const updated = prev.map((folder) =>
          folder.folderName === folderName
            ? { ...folder, files: [...auxList] }
            : folder
        );

        const pendingFolder = updated.find((f) => f.status === "pending");
        setCurrentUploadFolder(pendingFolder?.folderName || "");

        return updated;
      });

      setCurrentUploadItem(batch.map(([img]) => img.name).join(", "));

      try {
        const { data } = await api.post(
          `/missions/${missionId}/images/upload`,
          uploadData,
          { signal: abortController.signal }
        );

        data.forEach((result, index) => {
          const status = result["response"];
          auxList[startIndex + index][2] = "Enviado";
          if (status === "NO_GPS") {
            auxList[startIndex + index][1] = "Erro: Sem dados GPS";
          } else if (status === "ALREADY_EXIST") {
            auxList[startIndex + index][1] = "Erro: Já existente";
          } else {
            auxList[startIndex + index][1] = "Enviado";
          }
        });

        setFolderUploads((prev) =>
          prev.map((folder) =>
            folder.folderName === folderName
              ? { ...folder, files: [...auxList] }
              : folder
          )
        );

        return batch.length;
      } catch (error) {
        await deleteMission(missionId);
        throw error;
      }
    },
    []
  );

  const startUpload = async () => {
    if (folderUploads.length === 0) return;

    setStatusEnviandoFilaImagens(true);
    globalAbortController.current = new AbortController();

    let currentMissionId;

    try {
      for (
        let folderIndex = 0;
        folderIndex < folderUploads.length;
        folderIndex++
      ) {
        const folder = folderUploads[folderIndex];
        if (folder === null || folder.status === "completed") continue;

        const folderName = folder.folderName;

        // Create mission for this folder
        const { missionId, canceled } = await createMissionWithRetry(
          folder,
          folderIndex
        );

        if (canceled || missionId === null) continue;

        currentMissionId = missionId;

        // Update folder with mission ID
        setFolderUploads((prev) =>
          prev.map((f) =>
            f.folderName === folderName ? { ...f, missionId } : f
          )
        );

        const batchSize = 5;
        let successfulUploads = 0;
        const totalImages = folder.files.length;
        const auxList = [...folder.files];

        for (let i = 0; i < Math.ceil(totalImages / batchSize); i++) {
          if (globalAbortController.current?.signal.aborted) break;

          const batch = folder.files.slice(i * batchSize, (i + 1) * batchSize);
          const uploadedCount = await sendImages(
            folderName,
            batch,
            i * batchSize,
            auxList,
            globalAbortController.current,
            missionId
          );
          successfulUploads += uploadedCount;
        }

        if (successfulUploads === totalImages) {
          setFolderUploads((prev) =>
            prev.map((f) =>
              f.folderName === folderName ? { ...f, status: "completed" } : f
            )
          );
        }
      }

      if (folderUploads.every((folder) => folder.status === "completed")) {
        setStatusEnviandoFilaImagens(false);
        setMessage("Todas as imagens foram enviadas com sucesso!");
      }
    } catch (error) {
      console.log("Error in upload process:", error);
      if (currentMissionId) {
        deleteMission(currentMissionId);
      }
    } finally {
      updateMissions();
    }
  };

  const cancelUpload = async () => {
    if (globalAbortController.current) {
      globalAbortController.current.abort();

      setMessage("Envio cancelado.");
      toggleMessageModal();

      resetState();

      setTimeout(() => {
        updateMissions();
      });
    }
  };

  const resetState = () => {
    setFolderUploads([]);
    setCurrentUploadItem("");
    setStatusEnviandoFilaImagens(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const deleteMission = async (missionId) => {
    try {
      await api.delete(`/missions/${missionId}`);
    } catch (error) {
      console.error("Error deleting mission:", error);
    } finally {
      updateMissions();
    }
  };

  const calculateFolderProgress = (folderName) => {
    const folder = folderUploads.find((f) => f.folderName === folderName);
    if (!folder) return 0;

    const uploaded = folder.files.filter(
      ([, , status]) => status === "Enviado"
    ).length;
    return (uploaded / folder.files.length) * 100;
  };

  const countUploadedFilesInFolder = (folderName) => {
    const folder = folderUploads.find((f) => f.folderName === folderName);
    return folder
      ? folder.files.filter(([, , status]) => status === "Enviado").length
      : 0;
  };

  const countFilesInFolder = (folderName) => {
    const folder = folderUploads.find((f) => f.folderName === folderName);
    return folder ? folder.files.length : 0;
  };

  const countTotalFiles = () => {
    return folderUploads.reduce((sum, folder) => sum + folder.files.length, 0);
  };

  const calculateTotalProgress = () => {
    if (folderUploads.length === 0) return 0;

    const totalFiles = folderUploads.reduce(
      (sum, folder) => sum + folder.files.length,
      0
    );
    const uploadedFiles = folderUploads.reduce((sum, folder) => {
      return (
        sum + folder.files.filter(([, , status]) => status === "Enviado").length
      );
    }, 0);

    return totalFiles > 0 ? (uploadedFiles / totalFiles) * 100 : 0;
  };

  const countUploadedFiles = () => {
    return folderUploads.reduce((sum, folder) => {
      return (
        sum + folder.files.filter(([, , status]) => status === "Enviado").length
      );
    }, 0);
  };

  const renderUploadProgress = () => {
    const allCompleted =
      folderUploads.length > 0 &&
      folderUploads.every((folder) => folder.status === "completed");

    return (
      <>
        <div className="mb-3" style={{ width: "432px" }}>
          <div className="mb-2">
            <small>
              Progresso da pasta: <strong>{currentUploadFolder}</strong>
            </small>
            <Progress
              style={{ height: "20px" }}
              color="success"
              value={calculateFolderProgress(currentUploadFolder)}
            >
              {Math.round(calculateFolderProgress(currentUploadFolder))}%
            </Progress>
            <small>
              {countUploadedFilesInFolder(currentUploadFolder)}/
              {countFilesInFolder(currentUploadFolder)} arquivos
            </small>
          </div>

          <div className="mb-2">
            <small>Progresso total:</small>
            <Progress
              style={{ height: "20px" }}
              color="info"
              value={calculateTotalProgress()}
            >
              {Math.round(calculateTotalProgress())}%
            </Progress>
            <small>
              {countUploadedFiles()}/{countTotalFiles()} arquivos em{" "}
              {folderUploads.length} pastas
            </small>
          </div>
        </div>

        {allCompleted ? (
          <Button
            color="primary"
            outline
            className="col-12 mt-2"
            onClick={resetState}
          >
            Enviar mais arquivos!
          </Button>
        ) : (
          <Button outline className="col-12 mt-2" onClick={cancelUpload}>
            Cancelar Envio
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      {modal && <WarningModal modal={modal} setModal={setModal} />}

      {messageModal && (
        <ModalMessage
          isOpen={messageModal}
          message={message}
          toggle={toggleMessageModal}
        />
      )}

      <Form style={{ padding: 0 }}>
        <FormGroup>
          {!statusEnviandoFilaImagens && !currentUploadItem ? (
            <>
              <div>
                <div
                  {...getRootProps()}
                  style={{
                    border: "2px dashed #ccc",
                    borderRadius: "8px",
                    padding: "0 20px",
                    textAlign: "center",
                    background: "#f9f9f9",
                    cursor: "pointer",
                    width: "432px",
                    height: "80px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <input {...getInputProps()} webkitdirectory="" directory="" />
                  <MdOutlineCloudUpload size={24} />
                  <p style={{ margin: 0 }}>
                    Arraste pastas com imagens aqui para adicionar!
                  </p>
                </div>

                {folderUploads.length > 0 && (
                  <div className="col-12 mt-3 mb-3">
                    <div style={{ width: "100%" }}>
                      <small>
                        {folderUploads.length} pasta(s) selecionada(s) contendo{" "}
                        {countTotalFiles()} imagens
                      </small>
                    </div>
                    <div className="mt-2">
                      {folderUploads.map((folder, index) => (
                        <div
                          key={index}
                          className="mb-2 d-flex justify-content-between align-items-center"
                        >
                          <small>
                            <strong>{folder.folderName}</strong>:{" "}
                            {folder.files.length} imagem(ns)
                          </small>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => removeFolder(folder.folderName)}
                          >
                            Remover
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {folderUploads.length > 0 && (
                <Button
                  outline
                  className="col-12"
                  onClick={startUpload}
                  disabled={folderUploads.length === 0 || loading}
                >
                  Enviar {countTotalFiles()} imagens de {folderUploads.length}{" "}
                  pasta(s)
                </Button>
              )}
            </>
          ) : (
            renderUploadProgress()
          )}
        </FormGroup>
      </Form>
    </>
  );
});

export default ModalUploadImageNewMission;
